import * as React from "react";
import * as Material from "@material-ui/core";

type DialogConfig = {
  title?: string;
  content?: string | JSX.Element;
  buttons?: Array<{
    label: string;
    action?: () => void;
  }>;
  dismissable?: boolean;
};

const defaultDialogConfig: DialogConfig = {
  title: "",
  content: "",
  buttons: [],
  dismissable: true
};

export type DialogProp = {
  dialog: {
    prompt: (config?: DialogConfig) => void;
  };
};

type State = {
  dialogOpen: boolean;
  config: DialogConfig;
};

const withDialog = <P extends DialogProp>(
  WrappedComponent: React.ComponentType<P>
): React.ComponentClass<Omit<P, keyof DialogProp>> => {
  return class extends React.Component<Omit<P, keyof DialogProp>, State> {
    static displayName = `withCommentDialog(${WrappedComponent.displayName ||
      WrappedComponent.name})`;

    readonly props: P & DialogProp;
    readonly state = {
      dialogOpen: false,
      config: defaultDialogConfig
    };

    closeDialog() {
      this.setState({
        dialogOpen: false
      });
    }

    onClose() {
      this.setState({
        dialogOpen: false,
        config: defaultDialogConfig
      });
    }

    render() {
      const dialog = {
        prompt: (config: DialogConfig) => {
          const dialogConfig = {
            ...this.state.config,
            ...config
          };
          this.setState({
            dialogOpen: true,
            config: dialogConfig
          });
        }
      };

      return (
        <div>
          <WrappedComponent {...this.props} dialog={dialog} />

          <Material.Dialog
            open={this.state.dialogOpen}
            onClose={() => this.onClose()}
            onBackdropClick={() =>
              this.state.config.dismissable && this.closeDialog()
            }
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <Material.DialogTitle id="alert-dialog-title">
              {this.state.config.title}
            </Material.DialogTitle>
            <Material.DialogContent>
              {this.state.config.content}
            </Material.DialogContent>
            <Material.DialogActions>
              {this.state.config.buttons.map(button => {
                return (
                  <Material.Button
                    key={button.label}
                    onClick={() => {
                      button.action && button.action();
                      this.closeDialog();
                    }}
                    color="primary"
                  >
                    {button.label}
                  </Material.Button>
                );
              })}
            </Material.DialogActions>
          </Material.Dialog>
        </div>
      );
    }
  };
};

export default withDialog;
